import React, { useEffect, useState } from 'react';  
import { FaServer, FaCube, FaClock, FaHourglassStart, FaCheckCircle, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';  
import { Card, Badge, Row, Col, Spinner, Accordion, Button } from 'react-bootstrap';
import axios from 'axios';
import './EventItem.css';

const EventItem = ({ selectedCurrency }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    if (selectedCurrency && selectedCurrency.symbol) {
      setLoading(true);
      setError(null);

      axios
        .get(`/api/events/${selectedCurrency.algo}/${selectedCurrency.symbol}`)
        .then((response) => {
          setEvents(response.data);
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            setError('No events found for this algorithm and symbol.');
          } else {
            setError('An error occurred while fetching events.');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedCurrency]);

  const getIcon = (eventType) => {
    if (eventType === 'block_reward') {
      return <FaCube size={20} color="gold" />;  
    } else if (eventType === 'smartnode_collateral') {
      return <FaServer size={20} color="green" />;
    }
    return null;
  };

  const getStatusBadge = (eventStatus) => {
    switch (eventStatus) {
      case 'current':
        return (
          <div className="EventItem-status-container">
            <FaClock className="EventItem-clock-icon" />
            <Badge className="EventItem-animated-badge" bg="success">Current</Badge>
          </div>
        );
      case 'upcoming':
        return (
          <div className="EventItem-status-container">
            <FaHourglassStart className="EventItem-upcoming-icon" />
            <Badge bg="warning">Upcoming</Badge>
          </div>
        );
      case 'past':
        return (
          <div className="EventItem-status-container">
            <FaCheckCircle className="EventItem-past-icon" />
            <Badge bg="secondary">Past</Badge>
          </div>
        );
      default:
        return null;
    }
  };

  const getCardStyle = (eventStatus) => {
    switch (eventStatus) {
      case 'past':
        return 'EventItem-past-card';
      case 'current':
        return 'EventItem-current-card';
      case 'upcoming':
        return 'EventItem-upcoming-card';
      default:
        return '';
    }
  };

  const formatValue = (value) => {
    return value ? parseFloat(value) : null;
  };

  const currentEvents = events.filter(event => event.event_status === 'current');

  return (
    <div className="EventItem-container">
      <h3 className="EventItem-title">Events</h3>
      
      <Accordion className="mb-4">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <FaInfoCircle className="me-2" /> What are events?
          </Accordion.Header>
          <Accordion.Body>
            <p><strong>Halving:</strong> This is a scheduled event that occurs every certain number of blocks, cutting in half the reward that miners receive per block. The purpose of the Halving is to control inflation and reduce the number of new coins introduced into circulation, ensuring scarcity in the long term.</p>
            <p><strong>SmartNode Collateral:</strong> This is the amount of coins required to set up and operate a SmartNode in the network. The collateral ensures that the nodes involved in the process have an investment in the ecosystem and contribute to the stability of the network.</p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : error ? (
        <div className="text-center">
          <p>{error}</p>
        </div>
      ) : (
        <div>
          <Row className="g-4 justify-content-center mb-4">
            {currentEvents.length > 0 ? (
              currentEvents.map((event, index) => (
                <Col key={index} xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex justify-content-center">
                  <Card className={`EventItem-small-card mb-2 ${getCardStyle(event.event_status)}`}>
                    <Card.Body className="d-flex flex-column align-items-center p-2">
                      <div className="EventItem-icon-container mb-3">
                        {getIcon(event.event_type)}
                      </div>
                      <Card.Title className="EventItem-center-text">
                        {event.event_type === 'block_reward' ? 'Block Reward' : 'SmartNode Collateral'}
                      </Card.Title>
                      <div className="text-center mb-2">
                        {getStatusBadge(event.event_status)}
                      </div>
                      <div className="EventItem-event-details text-start">
                        <Card.Text className="mb-1 EventItem-small-text">
                          <strong>Block Range Start:</strong> {event.block_range_start}
                        </Card.Text>
                        <Card.Text className="mb-1 EventItem-small-text">
                          <strong>Block Range End:</strong> {event.block_range_end === 9.223372036854776e+18 ? 'No End' : event.block_range_end}
                        </Card.Text>
                        <Card.Text className="mb-1 EventItem-small-text">
                          <strong>Value Start:</strong> {formatValue(event.value_start)} {event.event_type === 'block_reward' ? 'Reward' : 'Collateral'}
                        </Card.Text>
                        {event.value_end && (
                          <Card.Text className="mb-1 EventItem-small-text">
                            <strong>Value End:</strong> {formatValue(event.value_end)} {event.event_type === 'block_reward' ? ' Reward' : 'Collateral'}
                          </Card.Text>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <p>No current events available.</p>
            )}

            <Col xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex justify-content-center align-items-center">
              <Button variant="primary" onClick={() => setShowAllEvents(!showAllEvents)}>
                {showAllEvents ? (
                  <>
                    <FaChevronUp className="me-2" /> Collapse Events
                  </>
                ) : (
                  <>
                    <FaChevronDown className="me-2" /> See All Events
                  </>
                )}
              </Button>
            </Col>
          </Row>
          {showAllEvents && (
            <Row className="g-4 justify-content-center mt-4">
              {events.map((event, index) => (
                <Col key={index} xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex justify-content-center">
                  <Card className={`EventItem-small-card mb-2 ${getCardStyle(event.event_status)}`}>
                    <Card.Body className="d-flex flex-column align-items-center p-2">
                      <div className="EventItem-icon-container mb-3">
                        {getIcon(event.event_type)}
                      </div>
                      <Card.Title className="EventItem-center-text">
                        {event.event_type === 'block_reward' ? 'Block Reward' : 'SmartNode Collateral'}
                      </Card.Title>
                      <div className="text-center mb-2">
                        {getStatusBadge(event.event_status)}
                      </div>
                      <div className="EventItem-event-details text-start">
                        <Card.Text className="mb-1 EventItem-small-text">
                          <strong>Block Range Start:</strong> {event.block_range_start}
                        </Card.Text>
                        <Card.Text className="mb-1 EventItem-small-text">
                          <strong>Block Range End:</strong> {event.block_range_end === 9.223372036854776e+18 ? 'No End' : event.block_range_end}
                        </Card.Text>
                        <Card.Text className="mb-1 EventItem-small-text">
                          <strong>Value Start:</strong> {formatValue(event.value_start)} {event.event_type === 'block_reward' ? 'Reward' : 'Collateral'}
                        </Card.Text>
                        {event.value_end && (
                          <Card.Text className="mb-1 EventItem-small-text">
                            <strong>Value End:</strong> {formatValue(event.value_end)} {event.event_type === 'block_reward' ? ' Reward' : 'Collateral'}
                          </Card.Text>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
    </div>
  );
};

export default EventItem;
